// src/components/ExerciseEdit.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import {
  IoArrowBack,
  IoAddOutline,
  IoTrashOutline,
  IoPlayCircleOutline,
  IoImageOutline,
  IoSaveOutline,
  IoCloudUploadOutline,
  IoLinkOutline,
} from 'react-icons/io5';

// ── Category config (same palette as Library) ─────────────────
const CATEGORIES = [
  { label: 'Pecho',     color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  { label: 'Espalda',   color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  { label: 'Piernas',   color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  { label: 'Glúteos',   color: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
  { label: 'Hombros',   color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  { label: 'Bíceps',    color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  { label: 'Tríceps',   color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { label: 'Core',      color: 'bg-teal-500/20 text-teal-300 border-teal-500/30' },
  { label: 'Cardio',    color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  { label: 'Full Body', color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
];

// Convert YouTube / similar URL → embed URL for preview
const getEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      return v ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (u.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
  } catch { /* ignore */ }
  return null;
};

const ExerciseEdit = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imgTab, setImgTab] = useState('url');   // 'url' | 'upload'
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const res = await api.get(`/admin/exercises/${exerciseId}`);
        const ex = res.data;
        setName(ex.name);
        setDescription(ex.description || '');
        setCategory(ex.category || '');
        setVideoUrl(ex.video_url || '');
        setImages(ex.images.map(img => img.url));
      } catch {
        toast.error('Error al cargar el ejercicio');
        navigate('/exercises');
      } finally {
        setLoading(false);
      }
    };
    fetchExercise();
  }, [exerciseId, navigate]);

  const handleImageChange = (index, value) => {
    const updated = [...images];
    updated[index] = value;
    setImages(updated);
  };
  const addImageField = () => setImages([...images, '']);
  const removeImageField = (index) => setImages(images.filter((_, i) => i !== index));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('La imagen supera el límite de 3 MB.');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato no permitido. Usa JPEG, PNG o WebP.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/admin/exercises/${exerciseId}/images/upload`, fd);
      setImages(prev => [...prev, res.data.url]);
      toast.success('Imagen subida correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/exercises/${exerciseId}`, {
        name,
        description,
        category,
        video_url: videoUrl,
        image_urls: images.filter(u => u && u.trim() !== ''),
      });
      toast.success('Ejercicio actualizado');
      navigate('/exercises');
    } catch {
      toast.error('Error al actualizar el ejercicio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-marizan-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const embedUrl = getEmbedUrl(videoUrl);
  const selectedCat = CATEGORIES.find(c => c.label.toLowerCase() === category.toLowerCase());

  return (
    <div className="fade-up max-w-5xl">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
      >
        <IoArrowBack size={16} />
        Volver a la biblioteca
      </button>

      {/* Title row */}
      <div className="flex items-center gap-3 mb-6">
        {selectedCat ? (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold border ${selectedCat.color}`}>
            {category.charAt(0).toUpperCase()}
          </div>
        ) : (
          <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 font-bold">
            ?
          </div>
        )}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">{name || 'Editar Ejercicio'}</h1>
          {category && <p className="text-sm text-gray-400">{category}</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left column (main fields) ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Nombre */}
            <div className="glass-panel rounded-xl p-5">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 block">
                Nombre del ejercicio *
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder="Ej: Sentadilla con Barra"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-marizan-red transition-colors text-lg font-medium"
              />
            </div>

            {/* Categoría */}
            <div className="glass-panel rounded-xl p-5">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 block">
                Categoría
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {CATEGORIES.map(c => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => setCategory(category === c.label ? '' : c.label)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      category === c.label
                        ? c.color + ' scale-105'
                        : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder="O escribe una categoría personalizada..."
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors"
              />
            </div>

            {/* Descripción */}
            <div className="glass-panel rounded-xl p-5">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 block">
                Descripción
              </label>
              <textarea
                rows={6}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe la ejecución correcta del ejercicio, músculos trabajados, consejos..."
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors resize-none leading-relaxed"
              />
            </div>
          </div>

          {/* ── Right column (video + images) ── */}
          <div className="space-y-5">

            {/* Video URL */}
            <div className="glass-panel rounded-xl p-5">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 block flex items-center gap-2">
                <IoPlayCircleOutline size={14} /> Video de referencia
              </label>
              <input
                type="url"
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors"
              />
              {/* Embed preview */}
              {embedUrl && (
                <div className="mt-3 rounded-xl overflow-hidden aspect-video">
                  <iframe
                    src={embedUrl}
                    title="Video preview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              )}
              {videoUrl && !embedUrl && (
                <p className="mt-2 text-xs text-gray-500">Vista previa no disponible para esta URL</p>
              )}
            </div>

            {/* Images */}
            <div className="glass-panel rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
                  <IoImageOutline size={14} /> Imágenes
                </label>
                {/* Tab switcher */}
                <div className="flex gap-1 bg-white/5 rounded-lg p-0.5 border border-white/10">
                  <button
                    type="button"
                    onClick={() => setImgTab('url')}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${imgTab === 'url' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                  >
                    <IoLinkOutline size={12} /> URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setImgTab('upload')}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${imgTab === 'upload' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                  >
                    <IoCloudUploadOutline size={12} /> Subir
                  </button>
                </div>
              </div>

              {/* URL tab */}
              {imgTab === 'url' && (
                <div className="space-y-2">
                  {images.map((url, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="url"
                        value={url}
                        onChange={e => handleImageChange(index, e.target.value)}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        className="flex-1 min-w-0 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-marizan-red transition-colors"
                      />
                      {url && (
                        <img
                          src={url}
                          alt=""
                          className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-white/10"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeImageField(index)}
                        className="flex-shrink-0 text-gray-500 hover:text-red-400 transition-colors p-1"
                      >
                        <IoTrashOutline size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addImageField}
                    className="mt-1 flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    <IoAddOutline size={14} /> Añadir URL de imagen
                  </button>
                </div>
              )}

              {/* Upload tab */}
              {imgTab === 'upload' && (
                <div className="space-y-3">
                  <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${uploading ? 'border-white/10 opacity-50 pointer-events-none' : 'border-white/15 hover:border-marizan-red/50 hover:bg-white/3'}`}>
                    <IoCloudUploadOutline size={28} className="text-gray-400" />
                    <span className="text-sm text-gray-300 font-medium">
                      {uploading ? 'Subiendo…' : 'Haz clic o arrastra una imagen'}
                    </span>
                    <span className="text-xs text-gray-500">JPEG, PNG o WebP · Máx 3 MB</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>

                  {/* Thumbnail grid */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {images.map((url, i) => (
                        <div key={i} className="relative group rounded-lg overflow-hidden border border-white/10">
                          <img src={url} alt="" className="w-full h-20 object-cover" onError={e => { e.target.style.display='none'; }} />
                          <button
                            type="button"
                            onClick={() => removeImageField(i)}
                            className="absolute top-1 right-1 bg-black/70 text-red-400 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <IoTrashOutline size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-marizan-red hover:bg-red-700 text-white font-semibold text-sm transition-colors disabled:opacity-50"
          >
            <IoSaveOutline size={16} />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExerciseEdit;
